import { assertUnreachable } from "common/utils/assertUnreachable";
import { degreeToRadian } from "common/utils/math";
import type { DrawingCamera, OrthographicDrawingCamera, PerspectiveDrawingCamera } from "doc/plans/drawingCameraTypes";
import { DrawingCameraType } from "doc/plans/drawingCameraTypes";
import { aabb2ByCenterWidthHeight, aabb2Center } from "geom/aabb2";
import { aabb2New, aabb2Height } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import type { PlaneBSP } from "geom/bspTypes";
import { type Mat4, mat4Ortho, mat4LookAt, mat4PerspectiveCropped } from "geom/mat4";
import { bspPerspectiveCroppedFrustum } from "geom/planeBSP";

export function aabb2GrowByMultiplier(bb: AABB2, paddingMultiplierX: number, paddingMultiplierY?: number): AABB2 {
  const [cx, cy] = aabb2Center(bb);
  const w = aabb2Width(bb) * paddingMultiplierX;
  const h = aabb2Height(bb) * (paddingMultiplierY ?? paddingMultiplierX);
  return aabb2ByCenterWidthHeight(cx, cy, w, h);
}

export function orthographicCameraCropBasedOnNDCBB(
  camera: OrthographicDrawingCamera,
  ndcBb: AABB2,
  paddingMultiplier: number,
): OrthographicDrawingCamera {
  ndcBb = aabb2GrowByMultiplier(ndcBb, paddingMultiplier);
  const originalWidth = camera.right - camera.left;
  const originalHeight = camera.top - camera.bottom;

  // Preserve aspect ratio, but fit the whole NDC BB in view.
  const [ndcCenterX, ndcCenterY] = aabb2Center(ndcBb);
  const ndcBbWidth = aabb2Width(ndcBb);
  const ndcBbHeight = aabb2Height(ndcBb);
  const maxDimensionNdcSize = Math.max(ndcBbWidth, ndcBbHeight);
  const ndcToRelative = (num: number) => {
    // NDC ranges from -1 to 1. Convert to range 0 to 1.
    return (num + 1) * 0.5;
  };
  const left = camera.left + ndcToRelative(ndcCenterX - maxDimensionNdcSize * 0.5) * originalWidth;
  const right = camera.left + ndcToRelative(ndcCenterX + maxDimensionNdcSize * 0.5) * originalWidth;
  const top = camera.bottom + ndcToRelative(ndcCenterY + maxDimensionNdcSize * 0.5) * originalHeight;
  const bottom = camera.bottom + ndcToRelative(ndcCenterY - maxDimensionNdcSize * 0.5) * originalHeight;
  return { ...camera, left, right, top, bottom };
}

export function perspectiveCameraCropBasedOnNDCBB(
  camera: PerspectiveDrawingCamera,
  ndcBb: AABB2,
  verticalFOVDegreesMax: number,
  paddingMultiplierY: number,
  paddingMultiplierX?: number,
): PerspectiveDrawingCamera {
  // In case the X padding is not specified, we fall back to the absolute padding size determined by paddingMultiplierY.
  paddingMultiplierX = paddingMultiplierX ?? 1.0 + (paddingMultiplierY - 1.0) / camera.aspect;

  ndcBb = aabb2GrowByMultiplier(ndcBb, paddingMultiplierX, paddingMultiplierY);
  const halfCameraFOV = degreeToRadian(camera.fovDegrees) / 2;
  const cameraFrustumHalfHeightPerZ = Math.tan(halfCameraFOV);
  const halfVerticalFOVRadiansMax = degreeToRadian(verticalFOVDegreesMax / 2);

  function clampAngle(angleRadians: number) {
    return Math.max(-halfVerticalFOVRadiansMax, Math.min(halfVerticalFOVRadiansMax, angleRadians));
  }

  // We clamp all angles according to the vertical FOV limit.
  // The horizontal angles computed are not technically correct, but aspect ratio effectively cancels out in the computations.
  const ndcMinYAngle = clampAngle(Math.atan(ndcBb.minY * cameraFrustumHalfHeightPerZ));
  const ndcMaxYAngle = clampAngle(Math.atan(ndcBb.maxY * cameraFrustumHalfHeightPerZ));
  const ndcMinXAngle = clampAngle(Math.atan(ndcBb.minX * cameraFrustumHalfHeightPerZ));
  const ndcMaxXAngle = clampAngle(Math.atan(ndcBb.maxX * cameraFrustumHalfHeightPerZ));

  const ndcMinXFromAngle = Math.tan(ndcMinXAngle) / cameraFrustumHalfHeightPerZ;
  const ndcMaxXFromAngle = Math.tan(ndcMaxXAngle) / cameraFrustumHalfHeightPerZ;
  const ndcMinYFromAngle = Math.tan(ndcMinYAngle) / cameraFrustumHalfHeightPerZ;
  const ndcMaxYFromAngle = Math.tan(ndcMaxYAngle) / cameraFrustumHalfHeightPerZ;
  const cropSize = Math.max(ndcMaxXFromAngle - ndcMinXFromAngle, ndcMaxYFromAngle - ndcMinYFromAngle);
  const centerX = (ndcMinXFromAngle + ndcMaxXFromAngle) / 2;
  const centerY = (ndcMinYFromAngle + ndcMaxYFromAngle) / 2;

  // CropSize is relative. Using the same cropSize across both dimensions preserves the same aspect ratio.
  const limitedNdcBb = aabb2ByCenterWidthHeight(centerX, centerY, cropSize, cropSize);

  const viewCropAABB = aabb2New(
    (limitedNdcBb.minX + 1) * 0.5,
    (limitedNdcBb.minY + 1) * 0.5,
    (limitedNdcBb.maxX + 1) * 0.5,
    (limitedNdcBb.maxY + 1) * 0.5,
  );
  return { ...camera, viewCropAABB };
}

export function drawingCameraToMatrices(camera: DrawingCamera): {
  projectionMatrix: Mat4;
  viewMatrix: Mat4;
  clippingPlanes?: PlaneBSP[];
} {
  if (camera.type === DrawingCameraType.PERSPECTIVE) {
    const viewMatrix = mat4LookAt(camera.eye, camera.target, camera.up);

    if (!(camera.near > 0)) {
      console.assert(false);
    }

    // First compute frustum bottom, left, width and height at the near plane without taking into account the crop
    let bottom = -camera.near * Math.tan(degreeToRadian(0.5 * camera.fovDegrees));
    let height = -2 * bottom;
    let width = camera.aspect * height;
    let left = -0.5 * width;

    // Crop with viewCropAABB
    left += camera.viewCropAABB.minX * width;
    bottom += camera.viewCropAABB.minY * height;
    width *= aabb2Width(camera.viewCropAABB);
    height *= aabb2Height(camera.viewCropAABB);

    const right = left + width;
    const top = bottom + height;

    return {
      projectionMatrix: mat4PerspectiveCropped(left, right, top, bottom, camera.near, camera.far),
      viewMatrix,
      clippingPlanes: bspPerspectiveCroppedFrustum(viewMatrix, left, right, top, bottom, camera.near, camera.far),
    };
  } else if (camera.type === DrawingCameraType.ORTHOGRAPHIC) {
    return {
      projectionMatrix: mat4Ortho(camera.left, camera.right, camera.bottom, camera.top, camera.near, camera.far),
      viewMatrix: mat4LookAt(camera.eye, camera.target, camera.up),
    };
  }
  assertUnreachable(camera);
}
